<template>
  <div>
    <QuoteNavTabs />
    <ContentView>
      <b-row class="pl-2">
        <b-col lg="4" class="mb-2 mb-lg-0">
          <!-- Substitute subheader -->
          <b-row style="margin-bottom: 2.5em;">
            <span class="font-lato fs-20">Mis Proyectos</span>
          </b-row>
          <b-row>
            <b-form-group style="width: 280px">
              <b-input-group size="sm">
                <b-input
                  v-model="searchTerm"
                  placeholder="Buscar por nombre proyecto o cliente"
                  @keyup.enter="search"
                />
                <b-input-group-append>
                  <b-button variant="outline-secondary" size="sm" @click="search">
                    <font-awesome-icon :icon="['fas', 'search']" fixed-width />
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
            <b-form-group class="ml-1 ml-sm-3">
              <b-button size="sm" @click="resetSearch">Reset</b-button>
            </b-form-group>
          </b-row>
        </b-col>
        <b-col lg="8" class="px-0">
          <!-- Status Filter -->
          <b-container id="status-filter-group" class="p-2 m-0">
            <b-row align-h="between" align-v="center" class="clickable px-4">
              <span class="fs-12" style="font-weight: 400">Filtro de Status:</span>
            </b-row>
            <hr class="mt-1 mb-2">
            <b-row class="px-4">
              <b-form-group class="m-0 px-1">
                <b-checkbox v-model="statusFilter" value="baja" size="sm" switch>Baja</b-checkbox>
                <b-checkbox v-model="statusFilter" value="media" size="sm" switch>Media</b-checkbox>
                <b-checkbox v-model="statusFilter" value="alta" size="sm" switch>Alta</b-checkbox>
              </b-form-group>
              <b-form-group class="m-0 px-1">
                <b-checkbox v-model="statusFilter" value="licitación" size="sm" switch>Licitación</b-checkbox>
                <b-checkbox v-model="statusFilter" value="vendido" size="sm" switch>Vendido</b-checkbox>
              </b-form-group>
              <b-form-group class="m-0 px-1">
                <b-checkbox v-model="statusFilter" value="desestimado" size="sm" switch>Desestimado</b-checkbox>
                <b-checkbox v-model="statusFilter" value="perdido" size="sm" switch>Perdido</b-checkbox>
              </b-form-group>
            </b-row>
          </b-container>
        </b-col>
      </b-row>
    </ContentView>
    <!-- Main Table -->
    <b-row align-v="center" class="mb-1 mx-0 pl-4">
      <div v-if="count" class="fs-12">
        <span v-if="queryMode" class="text-muted">{{ count.toLocaleString() }} resultados</span>
        <span v-else class="text-info">{{ count.toLocaleString() }} proyectos en total</span>
      </div>
    </b-row>
    <b-table
      :fields="tableFields"
      :items="projects"
      head-variant="light"
      fixed
      responsive
      show-empty
      no-local-sorting
      class="fs-13"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      @sort-changed="sortColumn"
    >
      <!-- Loading spinner / empty search text -->
      <template #empty>
        <div v-if="loading" class="text-center text-info">
          <b-spinner small type="grow"></b-spinner>
          <span class="pl-2">Cargando datos...</span>
        </div>
        <div v-else class="text-center text-danger">
          <span>No resultados</span>
        </div>
      </template>

      <!-- Table data -->
      <template v-slot:cell(user)="data">
        <span :class="{'text-danger': data.value.status == 'inactivo'}">{{ data.value | concatName2 | titleize }}</span>
      </template>

      <template v-slot:cell(client_name)="data">
        <span v-if="data.value">{{ data.value }}</span>
        <span v-else class="text-warning">
          <font-awesome-icon :icon="['far', 'exclamation-circle']" size="sm" fixed-width />
          <span>Falta Cliente</span>
        </span>
      </template>

      <template v-slot:cell(name)="data">
        <b-link :to="`/app/prod/proyectos/${data.item.id}`">
          <font-awesome-icon :icon="['far', 'briefcase']" size="lg" fixed-width />
          <span class="pl-1">{{ data.value }}</span>
        </b-link>
      </template>

      <template v-slot:cell(expected_revenue)="data">
        <span v-if="data.item.quotes.length > 0">{{ highestQuoteTotal(data.item.quotes) | dollarize }}</span>
        <span v-else>-</span>
      </template>

      <template v-slot:cell(status)="data">
        <b-badge
          pill
          :variant="data.value"
          class="clickable"
          style="font-size: 90%; width: 100px"
          @click="statusModalShow = true; openEditModal(data)"
        >
          <span>{{ data.value | titleize }}</span>
          <font-awesome-icon :icon="['fas', 'caret-down']" />
        </b-badge>
      </template>

      <template v-slot:cell(created_at)="data">
        {{ data.value | formatDateCompact }}
      </template>

      <template v-slot:cell(closing_date)="data">
        <span v-if="data.value" class="click-to-edit" @click="closingDateModalShow = true; openEditModal(data)">{{ data.value | formatDateCompact }}</span>
        <Button v-else size="sm" @click="closingDateModalShow = true; openEditModal(data)">
          <font-awesome-icon :icon="['far', 'calendar-edit']" size="lg" fixed-width />
        </Button>
      </template>

      <template v-slot:cell(billing_date)="data">
        <span v-if="data.value" class="click-to-edit" @click="billingDateModalShow = true; openEditModal(data)">{{ data.value | formatDateCompact }}</span>
        <Button v-else size="sm" @click="billingDateModalShow = true; openEditModal(data)">
          <font-awesome-icon :icon="['far', 'calendar-edit']" size="lg" fixed-width />
        </Button>
      </template>

      <!-- Row Details -->
      <template v-slot:cell(show_details)="row">
        <Button size="sm" style="width: 25px" @click="row.toggleDetails">
          <font-awesome-icon v-if="row.detailsShowing" :icon="['fas', 'caret-down']" size="lg" fixed-width />
          <font-awesome-icon v-else :icon="['fas', 'caret-right']" size="lg" fixed-width />
        </Button>
      </template>

      <template v-slot:row-details="row">
        <ul :class="ulMargin" class="fs-12 mb-0">
          <li v-for="quote in row.item.quotes.filter(q => q.status !== 'ignorar')" :key="quote.id" class="pb-2">
            <table class="table table-borderless table-sm brown-color m-0">
              <tr style="vertical-align: middle">
                <td class="p-0" style="width: 140px">
                  <b-link :to="`/app/prod/cotizador/${quote.id}`" class="pl-2 brown-color">Cotización {{ quote.quote_id }}</b-link>
                </td>
                <td class="p-0 text-center">|</td>
                <td class="p-0 text-center" style="width: 90px">
                  <span>{{ quote.total | dollarize }}</span>
                </td>
                <td class="p-0 text-center">|</td>
                <td class="p-0">
                  <span v-if="quote.title">{{ quote.title }}</span>
                  <span v-else class="font-italic">Sin Titulo</span>
                </td>
              </tr>
            </table>
          </li>
        </ul>
        <div class="d-flex w-100">
          <b-card :class="cardMargin" style="margin-top: 6px; width: 100%; margin-right: 15px">
            <b-row align-v="center" class="px-3">
              <span class="font-lato-th">Comentarios de Seguimiento</span>
              <Button size="sm" class="ml-3" @click="notesModalShow = true; openEditModal(row)">
                <font-awesome-icon :icon="['far', 'edit']" size="lg" fixed-width />
              </Button>
            </b-row>
            <b-row class="px-3 pt-1">
              <p class="text-info font-italic fs-14" style="margin-bottom: 0">{{ row.item.notes }}</p>
            </b-row>
          </b-card>
          <b-card v-if="row.item.status == 'vendido' || row.item.status == 'perdido' || row.item.status == 'desestimado'" style="margin-top: 6px; width: 480px">
            <b-row class="px-3">
              <span class="font-lato-th">Notas de cierre:</span>
              <span class="pl-3">{{ row.item.outcome_notes }}</span>
            </b-row>
          </b-card>
        </div>
      </template>
      <!-- Row Details (End) -->
    </b-table>

    <b-row align-h="center" class="my-5 mx-0">
      <PaginationCountless :page="page" :next="next" :prev="prev">
        <template #prev-label>
          <font-awesome-icon :icon="['fas', 'backward']" size="lg" fixed-width />
        </template>
        <template #next-label>
          <font-awesome-icon :icon="['fas', 'forward']" size="lg" fixed-width />
        </template>
      </PaginationCountless>
    </b-row>

    <!-- Status Modal -->
    <b-modal lazy v-model="statusModalShow">
      <template #modal-title>
        <h5>Cambiar status de "{{ selectedProject.name }}"</h5>
      </template>
      <b-container class="px-3 fs-14">
        <b-row align-h="center" class="my-3">
          <b-col lg="6" cols="7" class="border-right">
            <b-form-group>
              <b-radio-group v-model="form.status" :options="statusOptions" stacked></b-radio-group>
            </b-form-group>
          </b-col>
          <b-col lg="6" cols="5" class="pr-0 text-info">
            <small v-if="form.status === 'baja'">Baja probabilidad (menos de 50%)</small>
            <small v-if="form.status === 'media'">Media probabilidad (50 a 79%)</small>
            <small v-if="form.status === 'alta'">Alta probabilidad (más de 80%)</small>
            <small v-if="form.status === 'vendido'">Quedo abierta pero no se jalaron todas las líneas - CERRAR</small>
            <small v-if="form.status === 'desestimado'">El cliente no va a hacer nada (ni con nosotros, ni con nadie) - CERRAR</small>
            <small v-if="form.status === 'perdido'">El trabajo lo hizo otra empresa o persona.  Puede ser por precio, tiempo de respuesta de la gestión, especificaciones, plazo de entrega del producto, falta de tiempo del asesor, falla en la atención/servicio - CERRAR</small>
            <small v-if="form.status === 'licitación'">Especificación (mediano y largo plazo)</small>
          </b-col>
        </b-row>
        <b-row v-if="form.status === 'desestimado' || form.status ==='perdido'" align-h="center">
          <b-form-group label-size="sm" style="width: 350px">
            <template #label>
              <span>Razones</span><span class="asterisk">*</span>
            </template>
            <b-textarea v-model="form.outcome_notes" rows="6" autofocus size="sm" />
          </b-form-group>
        </b-row>
      </b-container>

      <template #modal-footer>
        <Button variant="green" class="float-right" :disabled="!statusFormValid" @click="updateProject">
          Guardar
        </Button>
      </template>
    </b-modal>

    <!-- Notes Modal -->
    <b-modal lazy v-model="notesModalShow" centered>
      <template #modal-title>
        <h6>Comentarios de "{{ selectedProject.name }}"</h6>
      </template>
      <b-row align-h="center">
        <b-textarea v-model="form.notes" autofocus size="sm" rows="6" style="width: 350px"></b-textarea>
      </b-row>
      <template #modal-footer>
        <Button variant="green" @click="updateProject">Guardar</Button>
      </template>
    </b-modal>

    <!-- Closing Date Modal -->
    <b-modal lazy v-model="closingDateModalShow" centered>
      <template #modal-title>
        <h6>Proyecto: {{ selectedProject.name }}</h6>
      </template>
      <b-row align-h="between" class="px-3 mb-3">
        <span class="fs-13" style="font-weight: 300">Eligir fecha de cierre en el calendario...</span>
        <Button v-if="form.closing_date" size="sm" @click="form.closing_date = null">
          <font-awesome-icon :icon="['far', 'undo-alt']" fixed-width />
          <span class="pl-1">Reset</span>
        </Button>
      </b-row>
      <b-row align-h="center">
        <flat-pickr
          v-model="form.closing_date"
          :config="flatpickrConfig"
          class="d-none"
        ></flat-pickr>
      </b-row>
      <b-row align-h="center" class="mt-4 mb-2" style="height: 30px">
        <p v-if="form.closing_date">{{ form.closing_date | formatDateLong }}</p>
        <p v-else class="font-italic text-danger">Sin fecha seleccionada</p>
      </b-row>
      <template #modal-footer>
        <Button variant="green" @click="updateProject">Guardar</Button>
      </template>
    </b-modal>

    <!-- Billing Date Modal -->
    <b-modal lazy v-model="billingDateModalShow" centered>
      <template #modal-title>
        <h6>Proyecto: {{ selectedProject.name }}</h6>
      </template>
      <b-row align-h="between" class="px-3 mb-3">
        <span class="fs-13" style="font-weight: 300">Eligir fecha de facturación en el calendario...</span>
        <Button v-if="form.billing_date" size="sm" @click="form.billing_date = null">
          <font-awesome-icon :icon="['far', 'undo-alt']" fixed-width />
          <span class="pl-1">Reset</span>
        </Button>
      </b-row>
      <b-row align-h="center">
        <flat-pickr
          v-model="form.billing_date"
          :config="flatpickrConfig"
          class="d-none"
        ></flat-pickr>
      </b-row>
      <b-row align-h="center" class="mt-4 mb-2" style="height: 30px">
        <p v-if="form.billing_date">{{ form.billing_date | formatDateLong }}</p>
        <p v-else class="font-italic text-danger">Sin fecha seleccionada</p>
      </b-row>
      <template #modal-footer>
        <Button variant="green" @click="updateProject">Guardar</Button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import flatPickr from 'vue-flatpickr-component';
import { Spanish } from "flatpickr/dist/l10n/es.js";
import Button from '../../shared/Button';
import ContentView from '../../shared/ContentView';
import PaginationCountless from '../../shared/PaginationCountless';
import Subheader from '../../shared/Subheader';
import QuoteNavTabs from '../quotes/QuoteNavTabs';

export default {
  name: 'ProjectIndex',
  components: { Button, ContentView, PaginationCountless, Subheader, QuoteNavTabs, flatPickr },
  data() {
    return {
      projectItems: [],
      page: 1,
      next: null,
      prev: null,
      count: null,
      searchTerm: '',
      queryMode: false,
      sortBy: 'created_at',
      sortDesc: true,
      form: {
        status: null,
        notes: '',
        closing_date: null,
        billing_date: null,
        // status followup
        outcome_notes: ''
      },
      selectedProject: null,
      selectedProjectIndex: null,
      statusModalShow: false,
      notesModalShow: false,
      closingDateModalShow: false,
      billingDateModalShow: false,
      fields: [
        { key: 'show_details', label: '', tdClass: 'text-center', thStyle: { width: '50px' } },
        { key: 'client_name', label: 'Cliente', thClass: 'font-lato-th', thStyle: { width: '300px' } },
        { key: 'name', label: 'Proyecto', thClass: 'font-lato-th', thStyle: { width: '300px' } },
        { key: 'expected_revenue', label: 'Ingreso potencial', tdClass: 'text-center', thClass: 'font-lato-th text-center', thStyle: { width: '100px' } },
        { key: 'status', sortable: true, tdClass: 'text-center', thClass: 'font-lato-th text-center', thStyle: { width: '150px' } },
        { key: 'next_task', label: '', thClass: 'font-lato-th text-center' },
        { key: 'created_at', label: 'Fecha creación', sortable: true, thClass: 'font-lato-th', thStyle: { width: '100px' } },
        { key: 'closing_date', label: 'Fecha cierre', thClass: 'font-lato-th', thStyle: { width: '80px' } },
        { key: 'billing_date', label: 'Fecha factur.', thClass: 'font-lato-th', thStyle: { width: '80px' } }
      ],
      supervisorFields: [
        { key: 'show_details', label: '', tdClass: 'text-center', thStyle: { width: '50px' } },
        { key: 'user', label: 'Asesor', tdClass: 'clickable', thClass: 'font-lato-th', thStyle: { width: '150px' } },
        { key: 'client_name', label: 'Cliente', thClass: 'font-lato-th', thStyle: { width: '300px' } },
        { key: 'name', label: 'Proyecto', thClass: 'font-lato-th', thStyle: { width: '300px' } },
        { key: 'expected_revenue', label: 'Ingreso potencial', tdClass: 'text-center', thClass: 'font-lato-th text-center', thStyle: { width: '100px' } },
        { key: 'status', sortable: true, tdClass: 'text-center', thClass: 'font-lato-th text-center', thStyle: { width: '150px' } },
        { key: 'next_task', label: '', thClass: 'font-lato-th text-center' },
        { key: 'created_at', label: 'Fecha creación', sortable: true, thClass: 'font-lato-th', thStyle: { width: '100px' } },  // previously width of 110px
        { key: 'closing_date', label: 'Fecha cierre', tdClass: 'text-center', thClass: 'font-lato-th text-center', thStyle: { width: '80px' } },
        { key: 'billing_date', label: 'Fecha factur.', tdClass: 'text-center', thClass: 'font-lato-th text-center', thStyle: { width: '80px' } }
      ],
      statusOptions: [
        { text: 'Baja probabilidad', value: 'baja' },
        { text: 'Media probabilidad', value: 'media' },
        { text: 'Alta probabilidad', value: 'alta' },
        { text: 'VENDIDO', value: 'vendido' },
        { text: 'DESESTIMADO', value: 'desestimado' },
        { text: 'PERDIDO', value: 'perdido' },
        { text: 'Licitación Construcción', value: 'licitación' }
      ],
      loading: false,
      processing: false,
      noScroll: false,
      flatpickrConfig: {
        locale: {
          ...Spanish,
          firstDayOfWeek: 0
        },
        inline: true,
        disableMobile: "true"
      }
    }
  },
  computed: {
    ...mapGetters(['isSupervisor']),
    statusFilter: {
      get() {
        return this.$store.state.projectStatusFilter;
      },
      set(val) {
        this.$store.commit('setProjectStatusFilter', val);
      }
    },
    projects() {
      let projects = this.projectItems
      if (projects) {
        projects = this.projectItems.map(project => {
          this.$set(project, '_showDetails', false);
          return project
        })
      }
      return projects
    },
    tableFields() {
      return this.isSupervisor ? this.supervisorFields : this.fields;
    },
    statusFormValid () {
      if (this.form.status === 'desestimado' || this.form.status === 'perdido') {
        return this.form.outcome_notes ? true : false;
      } else {
        return true;
      }
    },
    ulMargin() {
      return this.isSupervisor ? 'supervisor-ul-margin' : 'asesor-ul-margin';
    },
    cardMargin() {
      return this.isSupervisor ? 'supervisor-card-margin' : 'asesor-card-margin';
    }
  },
  watch: {
    '$route.query'() {
      this.fetchPaginatedProjects();
    },
    statusFilter(newVal, oldVal) {
      this.noScroll = true;
      this.fetchPaginatedProjects();
    }
  },
  mounted() {
    this.noScroll = true;
    this.fetchPaginatedProjects();
  },
  methods: {
    fetchPaginatedProjects() {
      this.loading = true;
      this.$http.get('/api/production/projects', {
          params: {
            page: this.$route.query.pagina,
            q: this.searchTerm,
            status_filter: this.statusFilter,
            sort_by: this.sortBy,
            sort_desc: this.sortDesc
          }
        })
        .then(response => {
          // console.log(response)
          this.projectItems = response.data.projects;
          this.page = response.data.pagy.page;
          this.next = response.data.pagy.next;
          this.prev = response.data.pagy.prev;
          this.count = response.data.pagy.count;
          if (!this.noScroll) {
            window.scrollTo({
              top: 0,
              left: 0,
              behavior: 'smooth'
            });
          }
        })
        .catch(error => {
          console.log(error)
        })
        .finally(() => {
          this.loading = false;
          this.noScroll = false;
        });
    },
    search() {
      if (this.processing) return;
      this.processing = true;
      let query = Object.assign({}, this.$route.query);
      delete query.pagina;
      this.$router.replace({ query });
      this.sortBy = 'created_at';
      this.sortDesc = true;
      this.$http.get('/api/production/projects', {
          params: {
            q: this.searchTerm.trim(),
            status_filter: this.statusFilter,
            sort_by: this.sortBy,
            sort_desc: this.sortDesc
          }
        })
        .then(response => {
          this.queryMode = true;
          // console.log(response)
          this.projectItems = response.data.projects;
          this.page = response.data.pagy.page;
          this.next = response.data.pagy.next;
          this.prev = response.data.pagy.prev;
          this.count = response.data.pagy.count;
        })
        .catch(error => {
          console.log(error)
        })
        .finally(() => this.processing = false);
    },
    resetSearch() {
      this.queryMode = false;
      this.searchTerm = '';
      this.sortBy = 'created_at';
      this.sortDesc = true;
      this.$router.push('/app/prod/proyectos');
      this.fetchPaginatedProjects();
    },
    sortColumn(ctx) {
      if (ctx.sortBy) {
        this.noScroll = true;
        this.sortBy = ctx.sortBy;
        this.sortDesc = ctx.sortDesc;
        this.fetchPaginatedProjects();
      }
    },
    highestQuoteTotal(quotes) {
      return Math.max(...quotes.filter(q => q.status !== 'ignorar').map(q => q.total), 0);
    },
    openEditModal(data) {
      this.form.outcome_notes = data.item.status === 'desestimado' || data.item.status === 'perdido' ? data.item.outcome_notes : null;
      this.form.status = data.item.status;
      this.form.notes = data.item.notes;
      this.form.closing_date = data.item.closing_date;
      this.form.billing_date = data.item.billing_date;
      this.selectedProject = data.item;
      this.selectedProjectIndex = data.index;
    },
    updateProject() {
      if (this.processing) return;
      this.processing = true;
      // const closedStatuses = ['vendido', 'desestimado', 'perdido'];
      // if (closedStatuses.includes(this.form.status) && !closedStatuses.includes(this.selectedProject.status)) {
      //   this.form.closing_date = new Date().toISOString().slice(0, 10)
      // }
      // if (!closedStatuses.includes(this.form.status) && closedStatuses.includes(this.selectedProject.status)) {
      //   this.form.closing_date = null;
      //   this.form.billing_date = null;
      // }
      this.$http.put(`/api/production/projects/${this.selectedProject.id}`, {
          project: this.form
        })
        .then(response => {
          // console.log(response)
          // TODO:  consolidate
          this.projectItems[this.selectedProjectIndex].status = response.data.status;
          this.projectItems[this.selectedProjectIndex].notes = response.data.notes;
          this.projectItems[this.selectedProjectIndex].closing_date = response.data.closing_date;
          this.projectItems[this.selectedProjectIndex].billing_date = response.data.billing_date;
          if (response.data.status == 'desestimado' || response.data.status == 'perdido') {
            this.projectItems[this.selectedProjectIndex].outcome_notes = response.data.outcome_notes;
          }
        })
        .catch(error => {
          console.log(error)
          // TODO:  alert
        })
        .finally(() => {
          this.processing = false;
          this.statusModalShow = false;
          this.notesModalShow = false;
          this.closingDateModalShow = false;
          this.billingDateModalShow = false;
        });
    }
  }
}
</script>

<style scoped>
#status-filter-group {
  border: 1px solid #dee2e6;
  border-radius: 3px;
  width: 360px;
}
.brown-color {
  color: rgb(121, 100, 101);
}
.asesor-ul-margin {
  margin-left: 340px;
}
.supervisor-ul-margin {
  margin-left: 490px;
}
.asesor-card-margin {
  margin-left: 50px;
}
.supervisor-card-margin {
  margin-left: 200px;
}
.form-control-sm {
  font-size: 12px !important;
  line-height: 2 !important;
}
/*Tree lines*/
ul, li {
  position: relative;
}
ul {
  list-style: none;
  padding-left: 35px;
}

li::before, li::after {
  content: "";
  position: absolute;
  left: -12px;
}
li::before {
  border-top: 1px solid rgb(121, 100, 101);
  top: 9px;
  width: 11px;
  height: 0;
}
li::after {
  border-left: 1px solid rgb(121, 100, 101);
  top: -10px;
  width: 0px;
  height: 100%;
}
ul > li:last-child::after {
  height: 20px;
}
</style>
